/**
 * Temporary contract-backed data for Student Insights.
 *
 * The UI consumes this shape as though it were returned by
 * GET /student/insights?enrollment_id=:enrollmentId. Keeping fixtures at this
 * boundary means replacing `getStudentInsightsDemo` with the real service will
 * not require reshaping view data inside the page.
 */

export type ObjectiveEvidenceState = "secure" | "building" | "not_assessed";

export interface EnrolledProductOption {
  enrollmentId: string;
  productId: string;
  productName: string;
  subject: string;
  gradeLabel: string;
}

export interface DomainCompletion {
  domainId: string;
  name: string;
  completedObjectives: number;
  totalObjectives: number;
}

export interface ObjectiveMasteryRow {
  objectiveId: string;
  objectiveName: string;
  evidenceState: ObjectiveEvidenceState;
  evidencedCriteriaCount: number;
  totalCriteriaCount: number;
  lastEvidenceAt: string | null;
}

export interface MasteryTrendPoint {
  periodStart: string;
  label: string;
  secureObjectives: number;
  totalObjectives: number;
}

export interface SessionInsight {
  sessionId: string;
  occurredAt: string;
  focus: string;
  durationMinutes: number;
  objectivesPractisedCount: number;
  learningOutcome: string;
  tutorNote: string;
}

export interface StudentInsightsResponse {
  enrollment: EnrolledProductOption;
  generatedAt: string;
  curriculum: {
    completedObjectives: number;
    totalObjectives: number;
    domains: DomainCompletion[];
    currentUnit: {
      unitId: string;
      name: string;
      completedObjectives: number;
      totalObjectives: number;
    };
  };
  sessions: {
    attendedCount: number;
    scheduledCount: number;
    attendedMinutes: number;
    objectivesPractisedCount: number;
    objectivesWithTutorEvidenceCount: number;
    recent: SessionInsight[];
  };
  objectiveMastery: ObjectiveMasteryRow[];
  masteryTrend: MasteryTrendPoint[];
}

export const studentInsightsFixtures: StudentInsightsResponse[] = [
  {
    enrollment: {
      enrollmentId: "enr-maths-mastery-year-9",
      productId: "product-maths-mastery",
      productName: "Maths Mastery",
      subject: "Mathematics",
      gradeLabel: "Year 9",
    },
    generatedAt: "2026-10-06T08:30:00.000Z",
    curriculum: {
      completedObjectives: 12,
      totalObjectives: 18,
      domains: [
        { domainId: "number-indices", name: "Number and indices", completedObjectives: 3, totalObjectives: 3 },
        { domainId: "algebra", name: "Algebra", completedObjectives: 4, totalObjectives: 6 },
        { domainId: "geometry", name: "Geometry", completedObjectives: 1, totalObjectives: 2 },
        { domainId: "measurement", name: "Measurement", completedObjectives: 2, totalObjectives: 3 },
        { domainId: "statistics", name: "Statistics", completedObjectives: 1, totalObjectives: 2 },
        { domainId: "functions", name: "Functions", completedObjectives: 1, totalObjectives: 2 },
      ],
      currentUnit: { unitId: "unit-algebra", name: "Algebra", completedObjectives: 4, totalObjectives: 8 },
    },
    sessions: {
      attendedCount: 6,
      scheduledCount: 8,
      attendedMinutes: 260,
      objectivesPractisedCount: 5,
      objectivesWithTutorEvidenceCount: 4,
      recent: [
        { sessionId: "ses-2026-06-12", occurredAt: "2026-06-12T15:00:00.000Z", focus: "Algebraic expressions", durationMinutes: 45, objectivesPractisedCount: 2, learningOutcome: "1 of 2 success criteria demonstrated", tutorNote: "Ready for independent practice" },
        { sessionId: "ses-2026-06-10", occurredAt: "2026-06-10T15:00:00.000Z", focus: "Linear equations", durationMinutes: 40, objectivesPractisedCount: 1, learningOutcome: "Independent practice demonstrated successfully", tutorNote: "Needs one more check" },
      ],
    },
    objectiveMastery: [
      { objectiveId: "lo-number-indices", objectiveName: "Number and indices", evidenceState: "secure", evidencedCriteriaCount: 4, totalCriteriaCount: 4, lastEvidenceAt: "2026-06-05T00:00:00.000Z" },
      { objectiveId: "lo-algebraic-expressions", objectiveName: "Algebraic expressions", evidenceState: "secure", evidencedCriteriaCount: 4, totalCriteriaCount: 4, lastEvidenceAt: "2026-06-12T00:00:00.000Z" },
      { objectiveId: "lo-linear-equations", objectiveName: "Linear equations", evidenceState: "building", evidencedCriteriaCount: 1, totalCriteriaCount: 4, lastEvidenceAt: "2026-06-10T00:00:00.000Z" },
      { objectiveId: "lo-inequalities", objectiveName: "Inequalities", evidenceState: "building", evidencedCriteriaCount: 2, totalCriteriaCount: 4, lastEvidenceAt: "2026-06-03T00:00:00.000Z" },
      { objectiveId: "lo-sequences", objectiveName: "Sequences", evidenceState: "not_assessed", evidencedCriteriaCount: 0, totalCriteriaCount: 5, lastEvidenceAt: null },
    ],
    masteryTrend: [
      { periodStart: "2026-01-01", label: "Jan", secureObjectives: 5, totalObjectives: 18 },
      { periodStart: "2026-02-01", label: "Feb", secureObjectives: 6, totalObjectives: 18 },
      { periodStart: "2026-03-01", label: "Mar", secureObjectives: 7, totalObjectives: 18 },
      { periodStart: "2026-04-01", label: "Apr", secureObjectives: 9, totalObjectives: 18 },
      { periodStart: "2026-05-01", label: "May", secureObjectives: 10, totalObjectives: 18 },
      { periodStart: "2026-06-01", label: "Jun", secureObjectives: 11, totalObjectives: 18 },
    ],
  },
  {
    enrollment: {
      enrollmentId: "enr-exam-prep-chemistry",
      productId: "product-exam-prep-chemistry",
      productName: "Exam Preparation",
      subject: "Chemistry",
      gradeLabel: "Year 9",
    },
    generatedAt: "2026-10-06T08:30:00.000Z",
    curriculum: {
      completedObjectives: 9,
      totalObjectives: 15,
      domains: [
        { domainId: "particles", name: "Particles", completedObjectives: 3, totalObjectives: 3 },
        { domainId: "reactions", name: "Reactions", completedObjectives: 3, totalObjectives: 4 },
        { domainId: "energetics", name: "Energetics", completedObjectives: 1, totalObjectives: 2 },
        { domainId: "analysis", name: "Analysis", completedObjectives: 1, totalObjectives: 2 },
        { domainId: "materials", name: "Materials", completedObjectives: 1, totalObjectives: 2 },
        { domainId: "earth", name: "Earth chemistry", completedObjectives: 0, totalObjectives: 2 },
      ],
      currentUnit: { unitId: "unit-reactions", name: "Chemical reactions", completedObjectives: 3, totalObjectives: 6 },
    },
    sessions: {
      attendedCount: 4,
      scheduledCount: 5,
      attendedMinutes: 210,
      objectivesPractisedCount: 4,
      objectivesWithTutorEvidenceCount: 3,
      recent: [
        { sessionId: "ses-chem-2026-06-11", occurredAt: "2026-06-11T15:00:00.000Z", focus: "Balancing equations", durationMinutes: 50, objectivesPractisedCount: 2, learningOutcome: "2 of 3 success criteria demonstrated", tutorNote: "Practise conserving atom counts" },
        { sessionId: "ses-chem-2026-06-04", occurredAt: "2026-06-04T15:00:00.000Z", focus: "Particle models", durationMinutes: 45, objectivesPractisedCount: 1, learningOutcome: "Independent explanation demonstrated successfully", tutorNote: "Ready to apply to new contexts" },
      ],
    },
    objectiveMastery: [
      { objectiveId: "lo-particle-models", objectiveName: "Particle models", evidenceState: "secure", evidencedCriteriaCount: 3, totalCriteriaCount: 3, lastEvidenceAt: "2026-06-04T00:00:00.000Z" },
      { objectiveId: "lo-balancing", objectiveName: "Balancing equations", evidenceState: "building", evidencedCriteriaCount: 2, totalCriteriaCount: 3, lastEvidenceAt: "2026-06-11T00:00:00.000Z" },
      { objectiveId: "lo-reactivity", objectiveName: "Reactivity series", evidenceState: "building", evidencedCriteriaCount: 1, totalCriteriaCount: 4, lastEvidenceAt: "2026-05-29T00:00:00.000Z" },
      { objectiveId: "lo-exothermic", objectiveName: "Exothermic reactions", evidenceState: "not_assessed", evidencedCriteriaCount: 0, totalCriteriaCount: 3, lastEvidenceAt: null },
    ],
    masteryTrend: [
      { periodStart: "2026-01-01", label: "Jan", secureObjectives: 3, totalObjectives: 15 },
      { periodStart: "2026-02-01", label: "Feb", secureObjectives: 4, totalObjectives: 15 },
      { periodStart: "2026-03-01", label: "Mar", secureObjectives: 5, totalObjectives: 15 },
      { periodStart: "2026-04-01", label: "Apr", secureObjectives: 7, totalObjectives: 15 },
      { periodStart: "2026-05-01", label: "May", secureObjectives: 8, totalObjectives: 15 },
      { periodStart: "2026-06-01", label: "Jun", secureObjectives: 9, totalObjectives: 15 },
    ],
  },
];

export async function getStudentInsightsDemo(enrollmentId: string): Promise<StudentInsightsResponse> {
  const insight = studentInsightsFixtures.find((item) => item.enrollment.enrollmentId === enrollmentId);
  if (!insight) throw new Error("No insight data is available for this enrolled product.");
  return Promise.resolve(insight);
}
